import { useEffect, useState } from 'react';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDispatch } from 'react-redux';
import {
  createTheme,
  ThemeProvider,
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Checkbox,
  CircularProgress,
  Alert,
} from '@mui/material';
import { clearError } from '../../store/slices/authSlice';
import { AppDispatch } from '../../store';
import logo from '../../assets/images/cacxi-white-logo.svg';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { organizationEndpoints } from '../../services/api/endpoints/organisation.endpoint';

import { NotificationT, SEVERITY } from '../../types/notification';
import Notifications from '../common/Notifications';
import { useCacxiStripe } from '../../hooks/dashboard/useStripe';
// Type for OAuth user info stored in session
interface OAuthUserInfo {
  email: string;
  firstName: string;
  userName: string;
  isGoogleUser: boolean;
}

const signupSchema = z.object({
  firstName: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  planId: z.string(),
  companyName: z.string().min(2, 'Organization name must be at least 2 characters'),
  useFreeTrial: z.boolean().optional(),
  acceptTerms: z.boolean(),
  promoCode: z.string().optional(),
});

type SignupFormValues = z.infer<typeof signupSchema>;

const SignupForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oauthProvider = searchParams.get('oauth'); // Check if coming from OAuth redirect
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [_useFreeTrial, setUseFreeTrial] = useState(true);
  const [_acceptTerms, setAcceptTerms] = useState(false);
  const [oauthUserInfo, setOauthUserInfo] = useState<OAuthUserInfo | null>(null);

  const {
    recurrencePlans,
  }: {
    recurrencePlans: {
      id: string;
      product: { name: string; description: string };
      unit_amount: number;
    }[];
  } = useCacxiStripe();

  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const dispatch = useDispatch<AppDispatch>();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SignupFormValues>({
    defaultValues: {
      firstName: '',
      email: '',
      companyName: '',
      planId: '',
      useFreeTrial: true,
      acceptTerms: undefined,
      promoCode: '',
    },
    resolver: zodResolver(signupSchema),
  });

  // Check for OAuth user info when redirected from login without organization
  useEffect(() => {
    if (oauthProvider === 'google') {
      const storedUserInfo = sessionStorage.getItem('oauth_user_info');
      if (storedUserInfo) {
        try {
          const userInfo: OAuthUserInfo = JSON.parse(storedUserInfo);
          setOauthUserInfo(userInfo);

          // Pre-fill the form with Google user's info
          if (userInfo.email) {
            setValue('email', userInfo.email);
          }
          if (userInfo.firstName) {
            setValue('firstName', userInfo.firstName);
          }

          // Clear the stored info
          sessionStorage.removeItem('oauth_user_info');
        } catch (e) {
          console.error('Failed to parse OAuth user info:', e);
        }
      }
    }
  }, [oauthProvider, setValue]);

  const watchUseFreeTrial = watch('useFreeTrial');
  const watchAcceptTerms = watch('acceptTerms');

  useEffect(() => {
    setAcceptTerms(!!watchAcceptTerms);
  }, [watchAcceptTerms]);

  useEffect(() => {
    setUseFreeTrial(!!watchUseFreeTrial);
  }, [watchUseFreeTrial]);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const lightTheme = createTheme({
    palette: {
      mode: 'dark',
    },
  });

  const onSubmit = async (data: SignupFormValues) => {
    try {
      const selectedPlanName = recurrencePlans.find(plan => plan.id === selectedPlan);
      if (!selectedPlanName) {
        throw new Error('Please select a plan');
      }

      const planType = selectedPlanName.product.name === 'Basic' ? 'rag' : 'scheduler';

      // Check if user came from OAuth (Google Sign-In)
      const isOAuthSignup = oauthUserInfo || oauthProvider === 'google';

      const response = await organizationEndpoints.requestOrganisation({
        companyName: data.companyName,
        email: data.email,
        firstName: data.firstName,
        planType: planType,
        planId: selectedPlan,
        isTrailEnabled: data.useFreeTrial,
        isOAuthUser: isOAuthSignup, // Mark as OAuth user if they came from Google
        ...(data.promoCode?.trim() && { dealerPromo: data.promoCode.trim() }),
      });

      if (response && response.isTrial && response.id) {
        // Store signup flow context so ActivateView knows the user came from signup
        sessionStorage.setItem('signup_flow', 'direct');
        sessionStorage.setItem('signup_is_oauth', isOAuthSignup ? 'true' : 'false');

        // Clear OAuth session data from sessionStorage
        if (isOAuthSignup) {
          sessionStorage.removeItem('oauth_user_info');
        }

        // Redirect directly to activation page
        navigate(`/activate/${response.id}`, { replace: true });
      } else if (response && response.checkoutUrl) {
        // Store org ID for post-payment activation redirect
        if (response.id) {
          sessionStorage.setItem('signup_flow', 'direct');
          sessionStorage.setItem('signup_org_id', response.id);
          sessionStorage.setItem('signup_is_oauth', isOAuthSignup ? 'true' : 'false');
        }
        // Redirect to Stripe checkout
        window.location.href = response.checkoutUrl;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      setNotification({
        open: true,
        message: error instanceof Error ? error.message : 'Signup failed',
        severity: SEVERITY.ERROR,
      });
    }
  };

  // Check for payment success/failure in URL params when component mounts
  useEffect(() => {
    const paymentSuccess = searchParams.get('payment_success');
    const paymentCancelled = searchParams.get('payment_cancelled');

    if (paymentSuccess === 'true') {
      // Check if we have a stored org ID from the signup flow
      const storedOrgId = sessionStorage.getItem('signup_org_id');
      if (storedOrgId) {
        sessionStorage.removeItem('signup_org_id');
        // Redirect to activation page after successful payment
        navigate(`/activate/${storedOrgId}`, { replace: true });
        return;
      }
      setNotification({
        open: true,
        message: 'Payment successful! Your account has been created.',
        severity: SEVERITY.SUCCESS,
      });
      // Clear the success param from URL
      navigate('/', { replace: true });
    } else if (paymentCancelled === 'true') {
      setNotification({
        open: true,
        message: 'Payment was cancelled. You can try again.',
        severity: SEVERITY.WARNING,
      });
      // Clear the cancelled param from URL
      navigate('/', { replace: true });
    }
  }, [navigate, searchParams]);

  const formDisabled = isSubmitting;

  useEffect(() => {
    // Auto-select the first available plan (single plan expected)
    if (recurrencePlans && recurrencePlans.length > 0) {
      const plan = recurrencePlans[0];
      setSelectedPlan(plan.id);
      setValue('planId', plan.id);
    }
  }, [recurrencePlans]);

  if (!recurrencePlans || recurrencePlans.length === 0) {
    return (
      <Box className="h-screen flex items-center justify-center font-sans login-bg ">
        <Typography className="text-white text-lg font-bold">Loading plans...</Typography>
      </Box>
    );
  }

  return (
    <Box className="min-h-screen flex items-center justify-center font-sans login-bg">
      <div className="w-full max-w-7xl flex flex-col lg:flex-row min-h-screen">
        <div className="lg:flex w-full hidden lg:w-1/2 flex-col justify-center items-center">
          <div className="text-white lg:p-16 flex flex-col justify-center items-center lg:items-start z-10 w-ful max-w-xl">
            <img className="text-white w-36 mb-4" src={logo} alt="CAC_logo" />
            <div className="hidden lg:flex flex-wrap">
              <Typography className="text-3xl lg:text-4xl font-bold leading-[3rem]">
                Smarter Solutions,
              </Typography>
              <Typography className="text-3xl lg:text-4xl font-bold leading-[3rem]">
                Seamless Growth
              </Typography>
            </div>
            <Typography className="hidden lg:block text-sm lg:text-lg font-normal">
              Revolutionize Bookings and boost Marketing with our AI powered smart tool
            </Typography>
            {/* <Box
              sx={{
                width: '90%',
                maxWidth: 600,
                mt: 4,
                display: { xs: 'none', lg: 'block' }, // hide on small screens
              }}
            >
              <video
                width="100%"
                height="auto"
                muted
                loop
                controls
                style={{ borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,.35)' }}
              >
                <source
                  src="https://s3.ap-southeast-2.amazonaws.com/cacxi.com.au/videos/Cacxi+Product+Demo.mp4"
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </Box> */}
          </div>
        </div>

        <Paper
          className="lg:grow h-full xs:p-4 sm:p-8 lg:p-10 xl:p-6 rounded-md bg-transparent flex flex-col justify-center items-center"
          elevation={0}
        >
          <Box
            sx={{
              maxWidth: 640,
              width: '100%',
              position: 'relative',
              backgroundColor: '#21172B',
              border: '1px solid #291c35',
              borderRadius: '12px',
              px: 5,
              py: 4,
            }}
          >
            {formDisabled && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: 'rgba(33, 23, 43, 0.95)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: '24px',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress size={60} thickness={4} sx={{ color: '#a855f7', mb: 3 }} />
                  <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: '1rem' }}>
                    {watchUseFreeTrial
                      ? 'Creating your trial account...'
                      : 'Loading payment system...'}
                  </Typography>
                </Box>
              </Box>
            )}

            <ThemeProvider theme={lightTheme}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <img
                  className="block lg:hidden text-white w-28 mb-6 mx-auto"
                  src={logo}
                  alt="CAC_logo"
                />
                <Typography
                  sx={{
                    color: 'text.primary',
                    fontSize: '1.75rem',
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Create Your Account
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                  Sign up to get started with our AI-powered smart tool
                </Typography>
              </Box>

              {/* Show alert if user came from Google OAuth without organization */}
              {oauthUserInfo && (
                <Alert
                  severity="info"
                  sx={{
                    mb: 3,
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    color: 'white',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    '& .MuiAlert-icon': {
                      color: '#60a5fa',
                    },
                  }}
                >
                  Welcome! You're signed in with Google as <strong>{oauthUserInfo.email}</strong>.
                  Please complete your organization setup below.
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <fieldset disabled={formDisabled} style={{ border: 'none', padding: 0, margin: 0 }}>
                  <TextField
                    margin="normal"
                    fullWidth
                    label="First Name"
                    {...register('firstName')}
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                      },
                    }}
                  />
                  <TextField
                    margin="normal"
                    fullWidth
                    label="Email"
                    {...register('email')}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={!!oauthUserInfo} // Disable if user came from Google OAuth
                    InputProps={{
                      readOnly: !!oauthUserInfo,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: oauthUserInfo
                          ? 'rgba(255, 255, 255, 0.02)'
                          : 'rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          backgroundColor: oauthUserInfo
                            ? 'rgba(255, 255, 255, 0.02)'
                            : 'rgba(255, 255, 255, 0.08)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                      },
                    }}
                  />
                  <TextField
                    margin="normal"
                    fullWidth
                    label="Your company Name"
                    {...register('companyName')}
                    error={!!errors.companyName}
                    helperText={errors.companyName?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                      },
                    }}
                  />
                  <TextField
                    margin="normal"
                    fullWidth
                    label="Promo Code (Optional)"
                    {...register('promoCode')}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    onChange={e => {
                      e.target.value = e.target.value.toUpperCase();
                      register('promoCode').onChange(e);
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                      },
                    }}
                  />

                  {recurrencePlans.length > 0 && (
                    <Box
                      sx={{
                        mt: 3,
                        mb: 2,
                        padding: 2.5,
                        border: '1px solid rgba(168, 85, 247, 0.3)',
                        borderRadius: '16px',
                        backgroundColor: 'rgba(168, 85, 247, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: 'rgba(255, 255, 255, 0.5)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            mb: 0.5,
                          }}
                        >
                          Your Plan
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            color: '#e9d5ff',
                          }}
                        >
                          {recurrencePlans[0].product.name}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 'bold',
                          color: '#c084fc',
                        }}
                      >
                        AUD ${(recurrencePlans[0].unit_amount / 100).toFixed(2)}
                        <Typography
                          component="span"
                          variant="subtitle2"
                          sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 0.5, fontWeight: 'normal' }}
                        >
                          /mo
                        </Typography>
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '16px',
                      padding: 2,
                      marginY: 2.5,
                      cursor: 'pointer',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.06)',
                        borderColor: 'rgba(168, 85, 247, 0.3)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <Box sx={{ pt: 0.5 }}>
                        <Controller
                          name="acceptTerms"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onChange={e => {
                                field.onChange(e.target.checked);
                              }}
                              sx={{
                                color: '#9333ea',
                                '&.Mui-checked': {
                                  color: '#a855f7',
                                },
                                '&:hover': {
                                  backgroundColor: 'rgba(168, 85, 247, 0.1)',
                                },
                              }}
                            />
                          )}
                        />
                      </Box>

                      <Box>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}
                        >
                          <Typography
                            fontWeight="bold"
                            color={watchAcceptTerms ? '#a855f7' : '#e9d5ff'}
                            sx={{ fontSize: '1rem' }}
                          >
                            I have read and accept the
                          </Typography>
                          <Typography
                            component="a"
                            href="https://cacxi.com/terms-and-conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                            fontWeight="bold"
                            sx={{
                              fontSize: '1rem',
                              color: '#a855f7',
                              textDecoration: 'underline',
                              '&:hover': { color: '#c084fc' },
                            }}
                          >
                            Terms & Conditions
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: watchUseFreeTrial ? '#a855f7' : 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '16px',
                      padding: 2.5,
                      marginY: 2,
                      backgroundColor: watchUseFreeTrial
                        ? 'rgba(168, 85, 247, 0.15)'
                        : 'rgba(255, 255, 255, 0.03)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#a855f7',
                        boxShadow: watchUseFreeTrial
                          ? '0 0 0 2px rgba(168, 85, 247, 0.3)'
                          : '0 0 0 1px rgba(168, 85, 247, 0.2)',
                        backgroundColor: 'rgba(168, 85, 247, 0.1)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                      }}
                    >
                      <Box sx={{ pt: 0.5 }}>
                        <Controller
                          name="useFreeTrial"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onChange={e => field.onChange(e.target.checked)}
                              sx={{
                                color: '#9333ea',
                                '&.Mui-checked': {
                                  color: '#a855f7',
                                },
                                '&:hover': {
                                  backgroundColor: 'rgba(168, 85, 247, 0.1)',
                                },
                              }}
                            />
                          )}
                        />
                      </Box>

                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography
                            fontWeight="bold"
                            color={watchUseFreeTrial ? '#a855f7' : '#e9d5ff'}
                            sx={{ fontSize: '1rem' }}
                          >
                            Try it free for 30 days!
                          </Typography>
                        </Box>

                        <Typography
                          variant="body2"
                          color={watchUseFreeTrial ? '#d8b4fe' : '#a1a1aa'}
                          sx={{ lineHeight: 1.4 }}
                        >
                          Get started risk-free with a 30-day trial. Cancel anytime before the trial
                          ends.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    type="submit"
                    fullWidth
                    disabled={isSubmitting || !selectedPlan || !_acceptTerms}
                    className="mt-4 bg-purple-800 py-2 text-md text-white font-bold hover:bg-purple-900"
                    sx={{
                      textTransform: 'none',
                      borderRadius: '12px',
                      py: 1.5,
                    }}
                  >
                    {isSubmitting
                      ? oauthUserInfo
                        ? 'Setting up your account…'
                        : 'Signing up…'
                      : !selectedPlan
                        ? 'Select a plan to continue'
                        : !_acceptTerms
                          ? 'Accept Terms & Conditions to continue'
                          : oauthUserInfo
                            ? 'Complete Setup'
                            : 'Sign Up with Email'}
                  </Button>
                </fieldset>
              </Box>

              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography
                  component="span"
                  sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}
                >
                  Already have an account?{' '}
                </Typography>
                <Button
                  onClick={() => navigate('/')}
                  disabled={formDisabled}
                  sx={{
                    fontSize: '0.875rem',
                    color: '#a855f7',
                    textTransform: 'none',
                    padding: 0,
                    minWidth: 'auto',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#c084fc',
                    },
                    '&:disabled': {
                      color: 'rgba(168, 85, 247, 0.4)',
                    },
                  }}
                >
                  Sign In
                </Button>
              </Box>
            </ThemeProvider>
          </Box>
        </Paper>
        <Notifications
          open={notification.open}
          message={notification.message}
          severity={notification.severity}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        />
      </div>
    </Box>
  );
};

export default SignupForm;
